/**
 * assets/fitment-results.js
 *
 * Controller for /pages/fitment — the fitment-filtered results page.
 *
 * Day 4b: client-side filter sidebar (brand, product type, price) + sort.
 * Day 4c: ?vehicle=<handle> URL parameter support.
 * Day 4d: Request-a-part modal wiring.
 * Day 4e: Pagination (48 per page, numbered nav).
 * Day 4f: Form submit success detection — Shopify /contact responds in
 *         ways that confuse strict fetch checks. Treat any non-thrown
 *         response as a successful submission.
 *
 * Pagination notes:
 *   - We render ALL fetched cards into the DOM at once but only the
 *     current page's slice is visible. Other cards have [data-page-hidden]
 *     applied which hides them via CSS. Filters/sort still operate on the
 *     full set instantly, page changes are CSS-only swaps.
 *   - Page resets to 1 whenever filters change, sort changes, or active
 *     vehicle changes.
 *   - Pagination nav is hidden when there's only one page of results.
 *
 * Flow:
 *   1. Read garage active vehicle. None → show "Add your car" state.
 *   2. Fetch (vehicle metafield) + (universal metafield, if toggle ON).
 *   3. Parse fragments, dedupe by handle.
 *   4. Inject into DOM, build filter options, apply filters/sort.
 *   5. Apply pagination: slice visible items, hide off-page.
 *   6. Render page nav.
 *   7. Dispatch 'chicane:badges:refresh'.
 *
 * Re-runs FETCH when:
 *   - Active garage vehicle changes (chicane:garage:changed event).
 *   - Universal toggle flips.
 *   - Retry button clicked.
 *
 * Re-applies FILTERS (no fetch) when filter checkbox toggled, sort
 * changed, or "Clear all" clicked. Each of these resets to page 1.
 *
 * Depends on:
 *   window.ChicaneFitment.Garage               (fitment-garage.js)
 *   window.ChicaneFitment.vehicles             (fitment-catalog snippet)
 *   sections/fitment-results.liquid            (markup this file controls)
 *   templates/collection.fitment-fragment.liquid (server-side fragment renderer)
 *   assets/fitment-badge.js                    (listens for chicane:badges:refresh)
 *   snippets/cc-request-part-modal.liquid      (request-a-part modal markup)
 *
 * Enable debug logs with:
 *   window.ChicaneFitmentDebug = true;
 */
(function () {
  'use strict';

  // ── Constants ────────────────────────────────────────────────────────────
  var EVENT_CHANGED       = 'chicane:garage:changed';
  var EVENT_BADGE_REFRESH = 'chicane:badges:refresh';
  var EVENT_GARAGE_READY  = 'chicane-garage:ready';
  var FRAGMENT_PATH       = '/collections/all';
  var FRAGMENT_VIEW       = 'fitment-fragment';
  var META_VEHICLE_KEY    = 'filter.p.m.custom.compatible_vehicles';
  var META_UNIVERSAL_KEY  = 'filter.p.m.custom.is_universal';
  var URL_PARAM_VEHICLE   = 'vehicle';
  var PAGE_SIZE           = 48;

  var PRICE_BUCKETS = [
    { id: 'under-100',   label: 'Under $100',      min: 0,       max: 10000   },
    { id: '100-250',     label: '$100 – $250',     min: 10000,   max: 25000   },
    { id: '250-500',     label: '$250 – $500',     min: 25000,   max: 50000   },
    { id: '500-1000',    label: '$500 – $1,000',   min: 50000,   max: 100000  },
    { id: 'over-1000',   label: 'Over $1,000',     min: 100000,  max: Infinity }
  ];

  function log() {
    if (window.ChicaneFitmentDebug && window.console && console.log) {
      console.log.apply(console, ['[fitment-results]'].concat([].slice.call(arguments)));
    }
  }

  // ── Garage helpers ──────────────────────────────────────────────────────
  function getGarage() {
    return (window.ChicaneFitment && window.ChicaneFitment.Garage) || null;
  }
  function getActiveVehicle() {
    var g = getGarage();
    if (!g || typeof g.getActive !== 'function') return null;
    try { return g.getActive(); } catch (e) { return null; }
  }
  function getVehicleId(v) {
    if (!v) return null;
    return v.id || v.gid || v.vehicleId || null;
  }
  function getVehicleName(v) {
    if (!v) return '';
    return v.displayName || v.display || v.name || v.title || '';
  }

  // ── Catalog lookup by handle (for ?vehicle= URL param) ──────────────────
  function findVehicleByHandle(handle) {
    if (!handle) return null;
    var catalog = window.ChicaneFitment && window.ChicaneFitment.vehicles;
    if (!catalog || !catalog.length) return null;
    for (var i = 0; i < catalog.length; i++) {
      if (catalog[i].handle === handle) return catalog[i];
    }
    return null;
  }

  function applyVehicleFromUrl() {
    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { return false; }

    var handle = params.get(URL_PARAM_VEHICLE);
    if (!handle) return false;

    var vehicle = findVehicleByHandle(handle);
    if (!vehicle) {
      log('?vehicle= handle not found in catalog:', handle);
      return false;
    }

    var garage = getGarage();
    if (!garage || typeof garage.addVehicle !== 'function') return false;

    var current = getActiveVehicle();
    if (current && current.id === vehicle.id) return true;

    log('Applying URL vehicle:', handle, '(', vehicle.displayName, ')');
    garage.addVehicle(vehicle.id);
    return true;
  }

  // ── URL building ────────────────────────────────────────────────────────
  function buildVehicleUrl(vehicleGid) {
    return FRAGMENT_PATH
      + '?' + META_VEHICLE_KEY + '=' + encodeURIComponent(vehicleGid)
      + '&view=' + FRAGMENT_VIEW;
  }
  function buildUniversalUrl() {
    return FRAGMENT_PATH
      + '?' + META_UNIVERSAL_KEY + '=1'
      + '&view=' + FRAGMENT_VIEW;
  }

  // ── Fetch + parse ───────────────────────────────────────────────────────
  function fetchFragment(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
      return res.text();
    }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var wrapper = doc.querySelector('[data-fitment-fragment]');
      if (!wrapper) {
        log('No [data-fitment-fragment] in response from', url);
        return { count: 0, items: [] };
      }
      var count = parseInt(wrapper.getAttribute('data-count'), 10) || 0;
      var items = [].slice.call(wrapper.querySelectorAll('.cc-fitment-fragment__item'));
      return { count: count, items: items };
    });
  }

  function getItemKey(itemEl) {
    var attr = itemEl.getAttribute('data-product-handle');
    if (attr) return attr;
    var link = itemEl.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var href = link.getAttribute('href') || '';
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function dedupeItems(items) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < items.length; i++) {
      var key = getItemKey(items[i]);
      if (!key) { out.push(items[i]); continue; }
      if (seen[key]) continue;
      seen[key] = true;
      out.push(items[i]);
    }
    return out;
  }

  function bucketFor(priceInCents) {
    for (var i = 0; i < PRICE_BUCKETS.length; i++) {
      var b = PRICE_BUCKETS[i];
      if (priceInCents >= b.min && priceInCents < b.max) return b.id;
    }
    return null;
  }

  // ── Controller ──────────────────────────────────────────────────────────
  function Controller(root) {
    this.root             = root;
    this.titleVehicleEl   = root.querySelector('[data-fitment-vehicle-name]');
    this.layoutEl         = root.querySelector('[data-fitment-layout]');
    this.sidebarEl        = root.querySelector('[data-fitment-sidebar]');
    this.sidebarToggleEl  = root.querySelector('[data-fitment-sidebar-toggle]');
    this.clearBtn         = root.querySelector('[data-fitment-clear]');
    this.clearFromStateBtn= root.querySelector('[data-fitment-clear-all-from-state]');
    this.activeCountEl    = root.querySelector('[data-fitment-active-count]');
    this.universalInput   = root.querySelector('[data-fitment-universal-toggle]');
    this.filterGroups     = {
      brand:       root.querySelector('[data-fitment-filter-group="brand"]'),
      productType: root.querySelector('[data-fitment-filter-group="productType"]'),
      price:       root.querySelector('[data-fitment-filter-group="price"]')
    };
    this.filterOptionContainers = {
      brand:       root.querySelector('[data-fitment-filter-options="brand"]'),
      productType: root.querySelector('[data-fitment-filter-options="productType"]'),
      price:       root.querySelector('[data-fitment-filter-options="price"]')
    };
    this.filterCountBadges = {
      brand:       root.querySelector('[data-fitment-filter-count-brand]'),
      productType: root.querySelector('[data-fitment-filter-count-productType]'),
      price:       root.querySelector('[data-fitment-filter-count-price]')
    };
    this.toolbarEl        = root.querySelector('[data-fitment-toolbar]');
    this.sortSelect       = root.querySelector('[data-fitment-sort]');
    this.countEl          = root.querySelector('[data-fitment-count]');
    this.resultsEl        = root.querySelector('[data-fitment-results]');
    this.paginationEl     = root.querySelector('[data-fitment-pagination]');
    this.stateEls         = {
      'no-garage':  root.querySelector('[data-fitment-state="no-garage"]'),
      'loading':    root.querySelector('[data-fitment-state="loading"]'),
      'empty':      root.querySelector('[data-fitment-state="empty"]'),
      'no-matches': root.querySelector('[data-fitment-state="no-matches"]'),
      'error':      root.querySelector('[data-fitment-state="error"]')
    };

    this.currentRequestId = 0;
    this.allItems         = [];
    this.activeFilters    = {
      brand:       new Set(),
      productType: new Set(),
      price:       new Set()
    };
    this.activeSort       = 'default';
    this.currentPage      = 1;

    this.wireEvents();
  }

  Controller.prototype.wireEvents = function () {
    var self = this;

    if (this.universalInput) {
      this.universalInput.addEventListener('change', function () {
        self.render();
      });
    }

    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', function () {
        self.activeSort = self.sortSelect.value;
        self.applySort();
        self.currentPage = 1;
        self.applyPagination();
      });
    }

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.clearAllFilters();
      });
    }

    if (this.clearFromStateBtn) {
      this.clearFromStateBtn.addEventListener('click', function () {
        self.clearAllFilters();
      });
    }

    if (this.sidebarToggleEl && this.sidebarEl) {
      this.sidebarToggleEl.addEventListener('click', function () {
        var isOpen = self.sidebarEl.getAttribute('data-open') === 'true';
        if (isOpen) {
          self.sidebarEl.removeAttribute('data-open');
          self.sidebarToggleEl.setAttribute('aria-expanded', 'false');
        } else {
          self.sidebarEl.setAttribute('data-open', 'true');
          self.sidebarToggleEl.setAttribute('aria-expanded', 'true');
        }
      });
    }

    var openBtn = this.root.querySelector('[data-fitment-open-garage]');
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        var g = getGarage();
        if (g && g.UI) {
          if (typeof g.UI.open   === 'function') { g.UI.open(); return; }
          if (typeof g.UI.show   === 'function') { g.UI.show(); return; }
          if (typeof g.UI.toggle === 'function') { g.UI.toggle(); return; }
        }
        document.dispatchEvent(new CustomEvent('chicane:garage:open'));
      });
    }

    var retryBtn = this.root.querySelector('[data-fitment-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        self.render();
      });
    }

    document.addEventListener(EVENT_CHANGED, function () {
      self.render();
    });
  };

  Controller.prototype.showState = function (which) {
    for (var key in this.stateEls) {
      if (this.stateEls[key]) this.stateEls[key].hidden = true;
    }

    if (which === 'results') {
      if (this.layoutEl)  this.layoutEl.hidden  = false;
      if (this.toolbarEl) this.toolbarEl.hidden = false;
      if (this.resultsEl) this.resultsEl.hidden = false;
      return;
    }

    if (this.paginationEl) this.paginationEl.hidden = true;

    if (which === 'no-garage') {
      if (this.layoutEl)  this.layoutEl.hidden  = false;
      if (this.toolbarEl) this.toolbarEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = true;
      if (this.sidebarEl) this.sidebarEl.style.display = 'none';
      if (this.sidebarToggleEl) this.sidebarToggleEl.style.display = 'none';
      if (this.stateEls['no-garage']) this.stateEls['no-garage'].hidden = false;
      return;
    }

    if (this.layoutEl)  this.layoutEl.hidden  = false;
    if (this.sidebarEl) this.sidebarEl.style.display = '';
    if (this.sidebarToggleEl) this.sidebarToggleEl.style.display = '';

    if (which === 'loading') {
      if (this.toolbarEl) this.toolbarEl.hidden = true;
      if (this.resultsEl) this.resultsEl.hidden = true;
    } else {
      if (this.toolbarEl) this.toolbarEl.hidden = (which === 'empty' || which === 'error');
      if (this.resultsEl) this.resultsEl.hidden = true;
    }

    if (this.stateEls[which]) this.stateEls[which].hidden = false;
  };

  Controller.prototype.updateHeader = function (vehicleName) {
    if (this.titleVehicleEl) {
      this.titleVehicleEl.textContent = vehicleName || 'your car';
    }
  };

  Controller.prototype.updateCount = function (visible, total) {
    if (!this.countEl) return;
    if (typeof visible !== 'number') {
      this.countEl.innerHTML = '&nbsp;';
      return;
    }
    if (typeof total === 'number' && visible !== total) {
      this.countEl.textContent = visible + ' of ' + total + ' products';
    } else {
      this.countEl.textContent = visible + (visible === 1 ? ' product' : ' products');
    }
  };

  Controller.prototype.buildFilterOptions = function () {
    var brandCounts = Object.create(null);
    var typeCounts  = Object.create(null);
    var priceCounts = Object.create(null);

    for (var i = 0; i < this.allItems.length; i++) {
      var el = this.allItems[i];
      var brand = (el.getAttribute('data-vendor') || '').trim();
      var ptype = (el.getAttribute('data-product-type') || '').trim();
      var price = parseInt(el.getAttribute('data-price'), 10);

      if (brand) brandCounts[brand] = (brandCounts[brand] || 0) + 1;
      if (ptype) typeCounts[ptype]  = (typeCounts[ptype]  || 0) + 1;
      if (!isNaN(price)) {
        var bid = bucketFor(price);
        if (bid) priceCounts[bid] = (priceCounts[bid] || 0) + 1;
      }
    }

    var brandOpts = Object.keys(brandCounts).sort(function (a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    }).map(function (name) {
      return { value: name, label: name, count: brandCounts[name] };
    });
    this.renderOptionList('brand', brandOpts);

    var typeOpts = Object.keys(typeCounts).sort(function (a, b) {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    }).map(function (name) {
      return { value: name, label: name, count: typeCounts[name] };
    });
    this.renderOptionList('productType', typeOpts);

    var priceOpts = [];
    for (var j = 0; j < PRICE_BUCKETS.length; j++) {
      var b = PRICE_BUCKETS[j];
      if (priceCounts[b.id]) {
        priceOpts.push({ value: b.id, label: b.label, count: priceCounts[b.id] });
      }
    }
    this.renderOptionList('price', priceOpts);
  };

  Controller.prototype.renderOptionList = function (groupKey, options) {
    var container = this.filterOptionContainers[groupKey];
    var groupEl   = this.filterGroups[groupKey];
    if (!container || !groupEl) return;

    while (container.firstChild) container.removeChild(container.firstChild);

    if (options.length < 2) {
      groupEl.hidden = true;
      return;
    }
    groupEl.hidden = false;

    var self = this;
    var previousSelected = this.activeFilters[groupKey];
    var newSelected = new Set();

    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      var label = document.createElement('label');
      label.className = 'cc-fitment-filter-option';

      var input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'cc-fitment-filter-option__input';
      input.value = opt.value;
      input.setAttribute('data-filter-group', groupKey);
      if (previousSelected && previousSelected.has(opt.value)) {
        input.checked = true;
        newSelected.add(opt.value);
      }

      var box = document.createElement('span');
      box.className = 'cc-fitment-filter-option__box';
      box.setAttribute('aria-hidden', 'true');

      var labelSpan = document.createElement('span');
      labelSpan.className = 'cc-fitment-filter-option__label';
      labelSpan.textContent = opt.label;

      var tally = document.createElement('span');
      tally.className = 'cc-fitment-filter-option__tally';
      tally.textContent = '(' + opt.count + ')';

      label.appendChild(input);
      label.appendChild(box);
      label.appendChild(labelSpan);
      label.appendChild(tally);
      container.appendChild(label);

      input.addEventListener('change', (function (gk, val, inp) {
        return function () {
          if (inp.checked) {
            self.activeFilters[gk].add(val);
          } else {
            self.activeFilters[gk]['delete'](val);
          }
          self.currentPage = 1;
          self.applyFilters();
        };
      })(groupKey, opt.value, input));
    }

    this.activeFilters[groupKey] = newSelected;
  };

  Controller.prototype.applyFilters = function () {
    var brandSel = this.activeFilters.brand;
    var typeSel  = this.activeFilters.productType;
    var priceSel = this.activeFilters.price;

    var visibleCount = 0;
    for (var i = 0; i < this.allItems.length; i++) {
      var el = this.allItems[i];
      var visible = true;

      if (brandSel.size > 0) {
        var brand = (el.getAttribute('data-vendor') || '').trim();
        if (!brandSel.has(brand)) visible = false;
      }
      if (visible && typeSel.size > 0) {
        var ptype = (el.getAttribute('data-product-type') || '').trim();
        if (!typeSel.has(ptype)) visible = false;
      }
      if (visible && priceSel.size > 0) {
        var price = parseInt(el.getAttribute('data-price'), 10);
        var bid   = isNaN(price) ? null : bucketFor(price);
        if (!bid || !priceSel.has(bid)) visible = false;
      }

      if (visible) {
        el.removeAttribute('data-filter-hidden');
        visibleCount++;
      } else {
        el.setAttribute('data-filter-hidden', '');
      }
    }

    this.updateActiveFilterBadges();

    if (visibleCount === 0 && this.allItems.length > 0) {
      this.updateCount(0, this.allItems.length);
      this.showState('no-matches');
      return;
    }

    if (this.allItems.length > 0) {
      this.showState('results');
    }

    this.applyPagination();
  };

  Controller.prototype.applyPagination = function () {
    var visibleItems = [];
    for (var i = 0; i < this.allItems.length; i++) {
      if (!this.allItems[i].hasAttribute('data-filter-hidden')) {
        visibleItems.push(this.allItems[i]);
      }
    }

    var totalVisible = visibleItems.length;
    var totalAll     = this.allItems.length;
    var pageCount    = Math.max(1, Math.ceil(totalVisible / PAGE_SIZE));

    if (this.currentPage > pageCount) this.currentPage = pageCount;
    if (this.currentPage < 1) this.currentPage = 1;

    var startIdx = (this.currentPage - 1) * PAGE_SIZE;
    var endIdx   = startIdx + PAGE_SIZE;

    for (var j = 0; j < visibleItems.length; j++) {
      if (j >= startIdx && j < endIdx) {
        visibleItems[j].removeAttribute('data-page-hidden');
      } else {
        visibleItems[j].setAttribute('data-page-hidden', '');
      }
    }

    this.updateCount(totalVisible, totalAll);
    this.renderPagination(pageCount);
  };

  Controller.prototype.renderPagination = function (pageCount) {
    if (!this.paginationEl) return;

    if (pageCount <= 1) {
      this.paginationEl.hidden = true;
      this.paginationEl.innerHTML = '';
      return;
    }

    this.paginationEl.hidden = false;
    var current = this.currentPage;

    var pages = [];
    function pushPage(n) {
      if (pages.length === 0 || pages[pages.length - 1] !== n) pages.push(n);
    }
    function pushEllipsis() {
      if (pages.length === 0 || pages[pages.length - 1] !== '...') pages.push('...');
    }

    pushPage(1);

    var rangeStart = Math.max(2, current - 1);
    var rangeEnd   = Math.min(pageCount - 1, current + 1);

    if (rangeStart > 2) pushEllipsis();
    for (var p = rangeStart; p <= rangeEnd; p++) pushPage(p);
    if (rangeEnd < pageCount - 1) pushEllipsis();

    if (pageCount > 1) pushPage(pageCount);

    var self = this;
    var frag = document.createDocumentFragment();

    for (var i = 0; i < pages.length; i++) {
      var entry = pages[i];
      if (entry === '...') {
        var span = document.createElement('span');
        span.className = 'cc-fitment-pagination__ellipsis';
        span.textContent = '…';
        span.setAttribute('aria-hidden', 'true');
        frag.appendChild(span);
      } else {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cc-fitment-pagination__btn';
        btn.textContent = entry;
        btn.setAttribute('data-page', entry);
        if (entry === current) {
          btn.classList.add('is-current');
          btn.setAttribute('aria-current', 'page');
          btn.disabled = true;
        }
        btn.addEventListener('click', (function (page) {
          return function () { self.goToPage(page); };
        })(entry));
        frag.appendChild(btn);
      }
    }

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'cc-fitment-pagination__btn';
    nextBtn.setAttribute('data-page-next', '');
    nextBtn.innerHTML = 'Next <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>';
    nextBtn.style.display = 'inline-flex';
    nextBtn.style.alignItems = 'center';
    nextBtn.style.gap = '4px';
    if (current >= pageCount) {
      nextBtn.disabled = true;
    } else {
      nextBtn.addEventListener('click', function () {
        self.goToPage(self.currentPage + 1);
      });
    }
    frag.appendChild(nextBtn);

    while (this.paginationEl.firstChild) this.paginationEl.removeChild(this.paginationEl.firstChild);
    this.paginationEl.appendChild(frag);
  };

  Controller.prototype.goToPage = function (n) {
    if (n === this.currentPage) return;
    this.currentPage = n;
    this.applyPagination();

    if (this.toolbarEl) {
      this.toolbarEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (this.resultsEl) {
      this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  Controller.prototype.applySort = function () {
    if (!this.resultsEl || this.allItems.length === 0) return;

    var sortKey = this.activeSort || 'default';

    if (!this._originalOrder) {
      this._originalOrder = this.allItems.slice();
    }

    var ordered;
    if (sortKey === 'default') {
      ordered = this._originalOrder.slice();
    } else {
      ordered = this.allItems.slice();

      ordered.sort(function (a, b) {
        if (sortKey === 'title-asc' || sortKey === 'title-desc') {
          var ta = getCardTitle(a);
          var tb = getCardTitle(b);
          var cmp = ta.toLowerCase().localeCompare(tb.toLowerCase());
          return sortKey === 'title-asc' ? cmp : -cmp;
        }
        if (sortKey === 'price-asc' || sortKey === 'price-desc') {
          var pa = parseInt(a.getAttribute('data-price'), 10);
          var pb = parseInt(b.getAttribute('data-price'), 10);
          if (isNaN(pa)) pa = sortKey === 'price-asc' ? Infinity : -1;
          if (isNaN(pb)) pb = sortKey === 'price-asc' ? Infinity : -1;
          return sortKey === 'price-asc' ? (pa - pb) : (pb - pa);
        }
        return 0;
      });
    }

    for (var i = 0; i < ordered.length; i++) {
      this.resultsEl.appendChild(ordered[i]);
    }
    this.allItems = ordered;
  };

  Controller.prototype.clearAllFilters = function () {
    this.activeFilters.brand.clear();
    this.activeFilters.productType.clear();
    this.activeFilters.price.clear();

    var inputs = this.root.querySelectorAll('[data-filter-group]');
    for (var i = 0; i < inputs.length; i++) inputs[i].checked = false;

    this.currentPage = 1;
    this.applyFilters();
  };

  Controller.prototype.updateActiveFilterBadges = function () {
    var total = 0;
    var groups = ['brand', 'productType', 'price'];
    for (var i = 0; i < groups.length; i++) {
      var k = groups[i];
      var n = this.activeFilters[k].size;
      total += n;
      var badge = this.filterCountBadges[k];
      if (badge) {
        if (n > 0) {
          badge.textContent = n;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }

    if (this.clearBtn) {
      this.clearBtn.hidden = (total === 0);
    }
    if (this.activeCountEl) {
      if (total > 0) {
        this.activeCountEl.textContent = total;
        this.activeCountEl.hidden = false;
      } else {
        this.activeCountEl.hidden = true;
      }
    }
  };

  Controller.prototype.renderCards = function (items) {
    if (!this.resultsEl) return;
    while (this.resultsEl.firstChild) {
      this.resultsEl.removeChild(this.resultsEl.firstChild);
    }
    for (var i = 0; i < items.length; i++) {
      this.resultsEl.appendChild(items[i]);
    }
    this.allItems = items;
    this._originalOrder = items.slice();

    try {
      document.dispatchEvent(new CustomEvent(EVENT_BADGE_REFRESH));
    } catch (e) {}
  };

  Controller.prototype.render = function () {
    var self = this;
    var active = getActiveVehicle();

    if (!active) {
      this.updateHeader('your car');
      this.updateCount(null);
      this.allItems = [];
      this._originalOrder = null;
      this.currentPage = 1;
      this.showState('no-garage');
      return;
    }

    var vehicleGid = getVehicleId(active);
    var vehicleName = getVehicleName(active);
    this.updateHeader(vehicleName);

    if (!vehicleGid) {
      log('Active vehicle has no ID — cannot query.');
      this.showState('error');
      return;
    }

    var includeUniversal = !!(this.universalInput && this.universalInput.checked);

    var myId = ++this.currentRequestId;
    this.showState('loading');
    this.updateCount(null);

    var fetches = [ fetchFragment(buildVehicleUrl(vehicleGid)) ];
    if (includeUniversal) {
      fetches.push(fetchFragment(buildUniversalUrl()));
    }

    Promise.all(fetches).then(function (results) {
      if (myId !== self.currentRequestId) {
        log('Stale response ignored');
        return;
      }

      var vehicleItems   = results[0] ? results[0].items : [];
      var universalItems = (results[1] && results[1].items) ? results[1].items : [];

      var combined = vehicleItems.concat(universalItems);
      var deduped  = dedupeItems(combined);

      log('Rendered', deduped.length,
          '(vehicle:', vehicleItems.length, 'universal:', universalItems.length, ')');

      if (deduped.length === 0) {
        self.allItems = [];
        self._originalOrder = null;
        self.currentPage = 1;
        self.updateCount(0);
        self.showState('empty');
        return;
      }

      self.currentPage = 1;
      self.renderCards(deduped);
      self.buildFilterOptions();
      self.applyFilters();

      if (self.activeSort && self.activeSort !== 'default') {
        self.applySort();
        self.applyPagination();
      }
    }).catch(function (err) {
      if (myId !== self.currentRequestId) return;
      console.error('[fitment-results] fetch failed:', err);
      self.showState('error');
    });
  };

  function getCardTitle(itemEl) {
    var titleEl = itemEl.querySelector('.cc-card-title-main')
               || itemEl.querySelector('.product-item-meta__title')
               || itemEl.querySelector('a[href*="/products/"]');
    return titleEl ? (titleEl.textContent || '').trim() : '';
  }

  // ── Request a part modal ────────────────────────────────────────────────
  function initRequestPartModal() {
    var modal = document.getElementById('cc-request-part-modal');
    if (!modal) return;

    var formWrap   = modal.querySelector('[data-cc-rqp-form-wrap]');
    var form       = modal.querySelector('[data-cc-rqp-form]')
                     || modal.querySelector('form');
    var successEl  = modal.querySelector('[data-cc-rqp-success]');
    var subjectIn  = modal.querySelector('#cc-rqp-subject');
    var vehicleIn  = modal.querySelector('#cc-rqp-vehicle');
    var vehicleHint = modal.querySelector('[data-cc-rqp-vehicle-hint]');
    var partNameIn = modal.querySelector('#cc-rqp-part-name');
    var submitBtn  = modal.querySelector('[data-cc-rqp-submit]');
    var submitLbl  = modal.querySelector('[data-cc-rqp-submit-label]');

    function open() {
      var active = getActiveVehicle();
      if (vehicleIn) {
        if (active) {
          vehicleIn.value = formatVehicleForForm(active);
          if (vehicleHint) {
            vehicleHint.textContent = 'Auto-filled from your saved garage. Edit if needed.';
          }
        } else {
          vehicleIn.value = '';
          if (vehicleHint) {
            vehicleHint.textContent = 'No vehicle saved — enter the year, make, model, and chassis if known.';
          }
        }
      }

      updateSubject();

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (formWrap) formWrap.hidden = false;
      if (successEl) successEl.hidden = true;

      setTimeout(function () {
        if (partNameIn) partNameIn.focus();
      }, 50);

      document.dispatchEvent(new CustomEvent('cc-request-part:opened'));
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.dispatchEvent(new CustomEvent('cc-request-part:closed'));
    }

    function formatVehicleForForm(v) {
      if (v.displayName) return v.displayName;
      var parts = [];
      if (v.year)  parts.push(v.year);
      if (v.make)  parts.push(v.make);
      if (v.model) parts.push(v.model);
      var label = parts.join(' ');
      if (v.submodel) label += ' (' + v.submodel + ')';
      return label;
    }

    function updateSubject() {
      if (!subjectIn) return;
      var partName = (partNameIn && partNameIn.value || '').trim();
      var vehicle  = (vehicleIn  && vehicleIn.value  || '').trim();
      var subject = 'Part request';
      if (partName && vehicle) {
        subject = 'Part request: ' + partName + ' for ' + vehicle;
      } else if (partName) {
        subject = 'Part request: ' + partName;
      } else if (vehicle) {
        subject = 'Part request for ' + vehicle;
      }
      subjectIn.value = subject;
    }

    function showSuccess() {
      if (formWrap) formWrap.hidden = true;
      if (successEl) successEl.hidden = false;
      try { form.reset(); } catch (e) {}
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-cc-rqp-open]');
      if (trigger) {
        e.preventDefault();
        open();
      }
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-cc-rqp-close]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) {
        close();
      }
    });

    if (partNameIn) partNameIn.addEventListener('input', updateSubject);
    if (vehicleIn)  vehicleIn.addEventListener('input', updateSubject);

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!submitBtn) return;

        updateSubject();

        submitBtn.disabled = true;
        var originalLabel = submitLbl ? submitLbl.textContent : 'Send request';
        if (submitLbl) submitLbl.textContent = 'Sending…';

        var formData = new FormData(form);

        // Shopify's /contact endpoint:
        //   - Receives the POST and sends the email server-side.
        //   - Returns various status codes / redirects depending on
        //     theme, locale, CDN setup. We've seen 200, 302, even
        //     opaque redirects via fetch.
        //   - The email lands successfully regardless of what fetch
        //     reports back.
        //
        // PRACTICAL POLICY: if fetch resolves at all (i.e. the request
        // reached Shopify's servers and got a response), treat it as
        // success. Only show an error if fetch THROWS — which means a
        // genuine network failure (offline, DNS, etc.).
        //
        // This is safe because:
        //   - Shopify's contact form has its own validation; if a field
        //     is missing it returns the form page with errors but the
        //     email isn't sent. The customer will see no email reply
        //     and can resubmit. False-positive success is preferable
        //     to false-positive failure (which is what we had before —
        //     emails arriving but customers thinking it failed).
        //   - Real network failures DO throw, so genuine errors are
        //     still caught.
        fetch(form.action || '/contact', {
          method: 'POST',
          body: formData
          // Note: removed the X-Requested-With header and Accept: json.
          // Shopify's /contact endpoint handles a "normal" form post
          // more reliably than an XHR-flagged one in our testing.
        }).then(function () {
          // ANY response = success. Don't inspect status codes.
          showSuccess();
        }).catch(function (err) {
          // Only genuine network failures land here.
          console.error('[Request a part] network error:', err);
          alert('Sorry — we couldn\'t reach our servers. Please check your connection and try again, or email sales@chicaneaustralia.com.au directly.');
        }).then(function () {
          submitBtn.disabled = false;
          if (submitLbl) submitLbl.textContent = originalLabel;
        });
      });
    }

    window.CCRequestPart = {
      open: open,
      close: close
    };
  }

  // ── Boot ────────────────────────────────────────────────────────────────
  function boot() {
    var root = document.querySelector('[data-fitment-page]');
    if (!root) return;

    initRequestPartModal();

    var booted = false;

    function start() {
      if (booted) return;
      booted = true;

      try { applyVehicleFromUrl(); }
      catch (e) { log('applyVehicleFromUrl threw:', e); }

      var controller = new Controller(root);
      controller.render();
    }

    document.addEventListener(EVENT_GARAGE_READY, start, { once: true });

    var attempts = 0;
    var maxAttempts = 40;
    var timer = setInterval(function () {
      attempts++;
      var hasGarage  = !!getGarage();
      var hasCatalog = !!(window.ChicaneFitment && window.ChicaneFitment.vehicles && window.ChicaneFitment.vehicles.length);

      if ((hasGarage && hasCatalog) || attempts >= maxAttempts) {
        clearInterval(timer);
        start();
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();